import React, { useState, useEffect, type ReactNode } from 'react';
import { initDemoFromUrl, demoRole, demoUser } from '@/lib/demo/demoMode';
import type { User, AuthState, LoginCredentials, RegisterData } from '@/types/Auth.types';
import { authApi } from '@/lib/api/auth';
import { saveTokens, clearTokens, getAccessToken, getRefreshToken } from '@/lib/utils/tokenStorage';
import { AuthContext } from './AuthContextType';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
    loading: true,
  });

  // Restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      // Demo sandbox: a public look-around identity. No tokens, no server —
      // the API layer answers from browser RAM (see lib/demo/).
      initDemoFromUrl();
      const dr = demoRole();
      if (dr) {
        setAuthState({
          user: demoUser(dr) as never,
          accessToken: 'demo',
          refreshToken: 'demo',
          isAuthenticated: true,
          loading: false,
        });
        return;
      }
      // Heal a stranded portal-preview session: the DevDashboard preview overlay swaps the admin
      // token in localStorage for a preview account's token and only restores it on explicit close.
      // If the page was refreshed/navigated during a preview, the admin token would be lost. When a
      // localStorage backup exists, restore it here (the overlay is gone after a reload anyway).
      const backupAccess = localStorage.getItem('adminAccessToken');
      const backupRefresh = localStorage.getItem('adminRefreshToken');
      if (backupAccess && backupRefresh) {
        localStorage.setItem('accessToken', backupAccess);
        localStorage.setItem('refreshToken', backupRefresh);
        localStorage.removeItem('adminAccessToken');
        localStorage.removeItem('adminRefreshToken');
      }

      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (accessToken && refreshToken) {
        try {
          const user = await authApi.getCurrentUser();
          localStorage.setItem('cachedUser', JSON.stringify(user));
          setAuthState({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
            loading: false,
          });
        } catch (err) {
          // Offline (no server response): keep the session on the last-known
          // user instead of logging out — dead-zone job sites must still open
          // the timesheet. Server rejections still clear the session below.
          const isNetwork = !!err && typeof err === 'object' && !('response' in err && (err as { response?: unknown }).response);
          const cached = localStorage.getItem('cachedUser');
          if (isNetwork && cached) {
            try {
              setAuthState({
                user: JSON.parse(cached),
                accessToken,
                refreshToken,
                isAuthenticated: true,
                loading: false,
              });
              return;
            } catch { /* fall through to logout */ }
          }
          clearTokens();
          setAuthState({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            loading: false,
          });
        }
      } else {
        setAuthState((prev) => ({ ...prev, loading: false }));
      }
    };

    restoreSession();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    const response = await authApi.login(credentials);
    
    if (response.success && response.data) {
      const { user, accessToken, refreshToken } = response.data;
      saveTokens(accessToken, refreshToken);
      setAuthState({
        user,
        accessToken,
        refreshToken,
        isAuthenticated: true,
        loading: false,
      });
    } else {
      throw new Error(response.error || 'Login failed');
    }
  };

  const register = async (data: RegisterData): Promise<void> => {
    const response = await authApi.register(data);
    
    if (response.success && response.data) {
      const { user, accessToken, refreshToken } = response.data;
      saveTokens(accessToken, refreshToken);
      setAuthState({
        user,
        accessToken,
        refreshToken,
        isAuthenticated: true,
        loading: false,
      });
    } else {
      throw new Error(response.error || 'Registration failed');
    }
  };

  const logout = async (): Promise<void> => {
    try {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearTokens();
      setAuthState({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        loading: false,
      });
    }
  };

  const setSession = (accessToken: string, refreshToken: string, user: User): void => {
    saveTokens(accessToken, refreshToken);
    setAuthState({ user, accessToken, refreshToken, isAuthenticated: true, loading: false });
  };

  const updateUser = (user: User): void => {
    setAuthState((prev) => ({ ...prev, user }));
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        setSession,
        register,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
